import { useEffect, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, ChevronUp, Folder, FolderOpen, Plus } from 'lucide-react';
import './FolderPicker.css';

function findPathNodes(nodes, targetId, trail = []) {
  for (const node of nodes || []) {
    const next = [...trail, node];
    if (node.id === targetId) return next;
    const sub = findPathNodes(node.children, targetId, next);
    if (sub) return sub;
  }
  return null;
}

/**
 * Tree folder picker. Select any existing folder (leaves included), or — when
 * `allowCreate` — create a new folder under any node (or at the root).
 *
 * Props:
 *   rootChildren       – top-level folder nodes (optional)
 *   onPathChange       – called with the current path string (excludes project)
 *   onNewFolderChange  – called with { parentFolderId, name } while creating a
 *                        new folder, or null when not (only fires if provided)
 *   allowCreate        – show the create ("+") affordances (default false)
 *   initialFolderId    – optional id to pre-select on first render
 */
export default function FolderPicker({ rootChildren, onPathChange, onNewFolderChange, allowCreate = false, initialFolderId }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null); // { id, path } | null (= no folder)
  const [creating, setCreating] = useState(null); // { parentId, parentPath } | null
  const [newName, setNewName] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    // Don't wipe an in-progress new-folder input when rootChildren transiently
    // goes empty during a tree reload (e.g. folderTree briefly null → []).
    if (creating && !rootChildren?.length) return;
    setCreating(null);
    setNewName('');
    onNewFolderChange?.(null);

    if (initialFolderId && rootChildren?.length) {
      const trail = findPathNodes(rootChildren, initialFolderId);
      if (trail) {
        const path = trail.map(n => n.name).join('/');
        setSelected({ id: initialFolderId, path });
        setExpanded(new Set(trail.slice(0, -1).map(n => n.id)));
        onPathChange(path);
        return;
      }
    }
    setSelected(null);
    setExpanded(new Set());
    onPathChange('');
  }, [rootChildren, initialFolderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function selectExisting(node, path) {
    setSelected({ id: node.id, path });
    setCreating(null);
    setNewName('');
    onNewFolderChange?.(null);
    onPathChange(path);
    setOpen(false);
  }

  function selectNone() {
    setSelected(null);
    setCreating(null);
    setNewName('');
    onNewFolderChange?.(null);
    onPathChange('');
    setOpen(false);
  }

  function startCreate(parentNode, parentPath) {
    setSelected(null);
    setCreating({ parentId: parentNode?.id ?? null, parentPath: parentPath || '' });
    setNewName('');
    onNewFolderChange?.({ parentFolderId: parentNode?.id ?? null, name: '' });
    onPathChange(parentPath || '');
  }

  function changeNewName(value) {
    const sanitized = value.replace(/\//g, '');
    setNewName(sanitized);
    onNewFolderChange?.({ parentFolderId: creating.parentId, name: sanitized });
    onPathChange(creating.parentPath ? `${creating.parentPath}/${sanitized}` : sanitized);
  }

  let label = 'No folder';
  let labelClass = 'placeholder';
  if (creating) {
    label = `${creating.parentPath ? creating.parentPath + '/' : ''}${newName || 'new folder'}`;
    labelClass = 'new';
  } else if (selected) {
    label = selected.path;
    labelClass = '';
  }

  function renderNodes(nodes, prefix) {
    return nodes.map(node => {
      const path = prefix ? `${prefix}/${node.name}` : node.name;
      const hasChildren = node.children?.length > 0;
      const isExpanded = expanded.has(node.id);
      const isSelected = selected?.id === node.id;
      const isCreatingHere = creating && creating.parentId === node.id;
      return (
        <div key={node.id}>
          <div className={`fp-row ${isSelected ? 'selected' : ''}`}>
            {hasChildren ? (
              <span className="fp-chevron" onClick={() => toggleExpand(node.id)}>
                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </span>
            ) : (
              <span className="fp-chevron-spacer" />
            )}
            <button type="button" className="fp-label" onClick={() => selectExisting(node, path)}>
              {isExpanded && hasChildren ? <FolderOpen size={13} /> : <Folder size={13} />}
              <span className="fp-name">{node.name}</span>
            </button>
            {allowCreate && (
              <button type="button" className="fp-add" title={`New folder in "${node.name}"`} onClick={() => startCreate(node, path)}>
                <Plus size={12} />
              </button>
            )}
          </div>
          {isCreatingHere && renderCreateInput()}
          {isExpanded && hasChildren && <div className="fp-children">{renderNodes(node.children, path)}</div>}
        </div>
      );
    });
  }

  function renderCreateInput() {
    return (
      <div className="fp-create">
        <input
          autoFocus
          className="fp-create-input"
          placeholder="New folder name"
          value={newName}
          onChange={e => changeNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setOpen(false); } }}
        />
        <button type="button" className="fp-create-ok" onClick={() => setOpen(false)}>Done</button>
      </div>
    );
  }

  return (
    <div className="fp-picker" ref={rootRef}>
      <button type="button" className="fp-control" onClick={() => setOpen(o => !o)}>
        <span className={`fp-control-label ${labelClass}`}>{label}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="fp-tree">
          <button type="button" className={`fp-none ${!selected && !creating ? 'selected' : ''}`} onClick={selectNone}>
            No folder
          </button>
          {renderNodes(rootChildren || [], '')}
          {allowCreate && (
            <>
              <button type="button" className="fp-add-root" onClick={() => startCreate(null, '')}>
                <Plus size={12} /> New folder at root
              </button>
              {creating && creating.parentId === null && renderCreateInput()}
            </>
          )}
        </div>
      )}
    </div>
  );
}
